import { useState } from 'react';

import {
  LinkIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  CursorArrowRaysIcon,
  DocumentTextIcon,
  CheckIcon,
  ClipboardIcon,
} from '@heroicons/react/24/outline';

const SERVER_URL = 'https://mcp.disgenet.com/mcp';

const FEATURES = [
  {
    icon: ShieldCheckIcon,
    title: 'Verifiable answers',
    body: 'Every gene-disease association comes with its DISGENET sources and confidence score, not a generic web search result.',
  },
  {
    icon: CursorArrowRaysIcon,
    title: 'Multi-step reasoning',
    body: 'The agent chains queries — searching genes, checking scores, cross-referencing variants — in a single session, seamlessly.',
  },
  {
    icon: LockClosedIcon,
    title: 'Traceable execution',
    body: 'The agent invokes named DISGENET tools, and the MCP server executes the queries, providing a transparent, reproducible record of every step.',
  },
  {
    icon: LinkIcon,
    title: 'Works where you work',
    body: 'Compatible with Claude, Cursor, and any MCP-enabled client. Combines naturally with tools for drug targets, clinical trials, and structural biology.',
  },
];

const STEPS = [
  {
    number: '01',
    icon: LinkIcon,
    title: 'Add the server',
    body: 'Add the DISGENET MCP server URL to your MCP-compatible assistant or application.',
    code: SERVER_URL,
  },
  {
    number: '02',
    icon: LockClosedIcon,
    title: 'Sign in',
    body: 'When your client prompts you, authenticate with your DISGENET account.',
    code: null,
  },
  {
    number: '03',
    icon: DocumentTextIcon,
    title: 'Ask in natural language',
    body: 'Once authenticated, you can query DISGENET directly. No endpoints, no manual parameters.',
    code: null,
  },
];

function CopyableCode({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Silent: if the browser blocks clipboard access, we don't break the UI.
    }
  };

  return (
    <button type="button" onClick={handleCopy} className="mcp-code" aria-label="Copy MCP server URL">
      <code>{value}</code>
      <span className="mcp-code__icon">
        {copied ? <CheckIcon className="w-4 h-4" /> : <ClipboardIcon className="w-4 h-4" />}
      </span>
    </button>
  );
}

export default function DisgenetMcpConnector() {
  return (
    <div className="mcp-connector">
      <style>{`
        .mcp-connector .mcp-diagram-wrap {
          background: var(--blue-color);
          border-radius: 16px;
          padding: 24px clamp(12px, 4vw, 32px);
          margin: 20px 0;
        }
        .mcp-diagram-wrap svg { display: block; }
        .mcp-node-title {
          font-weight: 600;
          font-size: 15px;
          fill: var(--blue-color);
        }
        .mcp-node-sub {
          font-size: 12px;
          fill: var(--secondary-gray-color);
        }

        .mcp-feature-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-top: 16px;
          margin-bottom: 20px;
        }
        .mcp-feature-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 18px;
        }
        .mcp-feature-card h3 {
          font-size: 15px;
          font-weight: 600;
          color: var(--blue-color);
          margin: 0 0 6px;
        }
        .mcp-feature-card p {
          font-size: 14px;
          margin: 0;
        }

        .mcp-steps { display: flex; flex-direction: column; margin-top: 8px; }
        .mcp-step {
          display: grid;
          grid-template-columns: 32px 1fr;
          gap: 14px;
          padding: 18px 0;
          border-top: 1px solid #e5e7eb;
        }
        .mcp-step:last-child { border-bottom: 1px solid #e5e7eb; }
        .mcp-step__num {
          font-weight: 600;
          color: var(--pink-color);
        }
        .mcp-step__head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
        .mcp-step__title { font-size: 15px; font-weight: 600; color: var(--blue-color); margin: 0; }
        .mcp-step p { margin: 4px 0 0; max-width: 60ch; }

        .mcp-code {
          margin-top: 10px;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          max-width: 100%;
          background: #f6f8fa;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 10px 12px;
          cursor: pointer;
        }
        .mcp-code code {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 14px;
          color: var(--blue-color);
          white-space: normal;
          word-break: break-all;
          text-align: left;
        }
        .mcp-code__icon { display: flex; flex-shrink: 0; color: var(--secondary-gray-color); }
        .mcp-code:hover .mcp-code__icon { color: var(--pink-color); }

        @media (max-width: 640px) {
          .mcp-feature-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <p>
        Connect Claude, Cursor, or any MCP-compatible client directly to DISGENET. The agent chooses among
        over a dozen tools instead of guessing API calls, and every answer comes with its supporting evidence
        and confidence score.
      </p>

      <div className="mcp-diagram-wrap" aria-hidden="true">
        <svg viewBox="0 0 900 200" width="100%" height="auto" role="img">
          <defs>
            <marker
              id="mcp-arrow"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 z" fill="#bcc0dd" />
            </marker>
          </defs>

          <path
            d="M232,100 L328,100"
            fill="none"
            stroke="#bcc0dd"
            strokeWidth="1.5"
            markerEnd="url(#mcp-arrow)"
          />
          <path
            d="M572,100 L668,100"
            fill="none"
            stroke="#bcc0dd"
            strokeWidth="1.5"
            markerEnd="url(#mcp-arrow)"
          />

          <circle r="4" fill="var(--pink-color)">
            <animateMotion dur="3.6s" repeatCount="indefinite" path="M232,100 L668,100" />
          </circle>

          <g>
            <rect x="20" y="55" width="212" height="90" rx="16" fill="#fff" stroke="#bcc0dd" />
            <text x="126" y="98" textAnchor="middle" className="mcp-node-title">
              AI Agent
            </text>
            <text x="126" y="118" textAnchor="middle" className="mcp-node-sub">
              Claude · Cursor · GPT
            </text>
          </g>

          <g>
            <rect
              x="328"
              y="55"
              width="244"
              height="90"
              rx="16"
              fill="#fff"
              stroke="var(--pink-color)"
              strokeWidth="1.4"
            />
            <text x="450" y="98" textAnchor="middle" className="mcp-node-title">
              MCP Connector
            </text>
            <text x="450" y="118" textAnchor="middle" className="mcp-node-sub">
              picks the appropriate tool
            </text>
          </g>

          <g>
            <rect
              x="668"
              y="55"
              width="212"
              height="90"
              rx="16"
              fill="#fff"
              stroke="#bcc0dd"
              strokeWidth="1.4"
            />
            <text x="774" y="98" textAnchor="middle" className="mcp-node-title">
              DISGENET
            </text>
            <text x="774" y="118" textAnchor="middle" className="mcp-node-sub">
              traceability + reproducibilty
            </text>
          </g>
        </svg>
      </div>

      <div className="divider"></div>

      <div className="mt-4">
        <h5 className="row">
          <CursorArrowRaysIcon className="w-6 h-6 mr-2 text-pink" />
          What changes vs. a REST API
        </h5>
        <p>
          The REST API is still there for deterministic integrations. The MCP connector solves a different
          problem: letting an agent reason about which data it needs, without inventing endpoints.
        </p>
        <div className="mcp-feature-grid">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div className="mcp-feature-card" key={title}>
              <Icon className="w-5 h-5 mb-2 text-pink" />
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="divider"></div>

      <div className="mt-4">
        <h5 className="row">
          <LinkIcon className="w-6 h-6 mr-2 text-pink" />
          Connect in 3 steps
        </h5>
        <p>Available for any DISGENET account, no additional setup required.</p>
        <div className="mcp-steps">
          {STEPS.map(step => (
            <div className="mcp-step" key={step.number}>
              <span className="mcp-step__num">{step.number}</span>
              <div>
                <div className="mcp-step__head">
                  <step.icon className="w-5 h-5 text-pink" />
                  <h3 className="mcp-step__title">{step.title}</h3>
                </div>
                <p>{step.body}</p>
                {step.code && <CopyableCode value={step.code} />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
